import type { ReactNode } from "react"

interface SignalGroup {
  title: string
  tone: "good" | "bad"
  icon?: ReactNode
  items: string[]
}

interface SignalPairProps {
  /** Two side-by-side signal groups (typically "Do this" / "Don't do this") */
  groups: [SignalGroup, SignalGroup]
  className?: string
}

/**
 * SignalPair — two-column card layout for paired pros/cons or
 * repair/replace type lists. Each side gets its own tone color (green/orange)
 * and a title + bulleted items.
 */
export function SignalPair({ groups, className = "" }: SignalPairProps) {
  return (
    <div className={`grid md:grid-cols-2 gap-4 ${className}`}>
      {groups.map((g, i) => (
        <div
          key={i}
          className={`rounded-xl border p-5 ${
            g.tone === "good"
              ? "border-green-200 bg-green-50/50"
              : "border-[#ff9951]/30 bg-[#ff9951]/5"
          }`}
        >
          <div className="flex items-center gap-2 mb-3">
            {g.icon && (
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center text-white ${
                  g.tone === "good" ? "bg-green-600" : "bg-[#ff9951]"
                }`}
              >
                {g.icon}
              </div>
            )}
            <h3 className="text-base font-black tracking-tight text-gray-900">{g.title}</h3>
          </div>
          <ul className="space-y-1.5">
            {g.items.map((item, j) => (
              <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                <span
                  className={`w-4 h-4 flex-shrink-0 mt-0.5 font-bold ${
                    g.tone === "good" ? "text-green-600" : "text-[#ff9951]"
                  }`}
                >
                  {g.tone === "good" ? "✓" : "✕"}
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

export type { SignalGroup }
